import {Injectable} from '@angular/core';
import {Observable, of} from "rxjs";
import {BaseListResponse, BaseModel} from "../models/BaseModel";
import {environment} from "../../environments/environment";
import {HttpClient} from "@angular/common/http";
import {Assignment, AssignmentDetail} from "../models/assignment/assignment";

import {
  GenerateFWASigningLinkRequest, GenerateVerifiedSigningLinkResponse,
  VerifiedSignedStatusResponse
} from "../models/assignment/GenerateFWASigningLinkRequest";
import {PSAStatusResponse} from "../models/grantApplication/generatePSASigningLinkRequest";
import {baseFile} from "../models/grantApplication/grantApplicationGetinfoResponse";
import {ApplicationFileInfo} from "../models/grantApplication/grantApplicationResponse";
import {lorem} from "../utils/lorem";


@Injectable({
  providedIn: 'root'
})
export class AssignmentService {

  private apiUrl = environment.apiConfig.uri+ '/api/assignment';

  constructor(private http: HttpClient) { }

  getAssignmentList(page : number, limit: number, sortColumn : string): Observable<BaseModel<BaseListResponse<Assignment>>> {
    return this.http.get<BaseModel<BaseListResponse<Assignment>>>(`${this.apiUrl}/query/${page}/${limit}?sort=${sortColumn}`);
  }


  getInfo(id: string):Observable<BaseModel<AssignmentDetail>> {
    return this.http.get<BaseModel<AssignmentDetail>>(`${this.apiUrl}/getinfo/${id}`);
  }

  Generatefwalink(request: GenerateFWASigningLinkRequest): Observable<BaseModel<GenerateVerifiedSigningLinkResponse>> {
    return this.http.post<BaseModel<GenerateVerifiedSigningLinkResponse>>(`${this.apiUrl}/generatefwalink`, request);
  }
  getDocsStatus(contractsignaturedetailId: string|undefined):Observable<BaseModel<PSAStatusResponse>> {
    return this.http.get<BaseModel<PSAStatusResponse>>(`${this.apiUrl}/getfwasigningstatus/${contractsignaturedetailId}`);
  }


  getSignedDocuments(applicationId: string | undefined):Observable<BaseModel<baseFile>> {
    return this.http.get<BaseModel<baseFile>>(`${this.apiUrl}/getsigneddocuments/${applicationId}`);
  }

  downloadFwaFile(fileId: string): Observable<BaseModel<ApplicationFileInfo>> {
    return this.http.get<BaseModel<ApplicationFileInfo>>(`${this.apiUrl}/download/${fileId}`);
  }

  // Mock version of getAssignmentList: returns static data with simple pagination and optional sorting
  getAssignmentListMock(page: number, limit: number, sortColumn: string): Observable<BaseModel<BaseListResponse<Assignment>>> {
    const mockItems: Assignment[] = [
      {
        id: 'A-1001',
        name: 'Data Collection for Survey',
        jobDescription: lorem(1000),
        fromDate: new Date('2025-01-10'),
        toDate: new Date('2025-03-15'),
        projectId: 'P-2001',
        projectName: 'Community Health Assessment',
        statusName: 'AwaitingSignatures'
      },
      {
        id: 'A-1002',
        name: 'Statistical Analysis Support',
        jobDescription: lorem(1000),
        fromDate: new Date('2025-02-01'),
        toDate: new Date('2025-04-30'),
        projectId: 'P-2002',
        projectName: 'Nutrition Program Evaluation',
        statusName: 'Draft'
      },
      {
        id: 'A-1003',
        name: 'Reporting and Documentation',
        jobDescription: lorem(1000),
        fromDate: new Date('2025-03-01'),
        toDate: new Date('2025-05-31'),
        projectId: 'P-2003',
        projectName: 'Maternal Health Initiative',
        statusName: 'Finalized'
      },
      {
        id: 'A-1004',
        name: 'Workshop Facilitation',
        jobDescription: lorem(1000),
        fromDate: new Date('2025-01-20'),
        toDate: new Date('2025-02-20'),
        projectId: 'P-2004',
        projectName: 'Capacity Building Program',
        statusName: 'Signed'
      },
      {
        id: 'A-1005',
        name: 'Monitoring & Evaluation',
        jobDescription: lorem(1000),
        fromDate: new Date('2025-04-01'),
        toDate: new Date('2025-06-30'),
        projectId: 'P-2005',
        projectName: 'Rural Clinic Outreach',
        statusName: 'Draft'
      },
      {
        id: 'A-1006',
        name: 'Field Coordination',
        jobDescription: lorem(1000),
        fromDate: new Date('2025-02-15'),
        toDate: new Date('2025-05-15'),
        projectId: 'P-2006',
        projectName: 'Vaccination Drive',
        statusName: 'AwaitingSignatures'
      },
      {
        id: 'A-1007',
        name: 'Qualitative Research',
        jobDescription: lorem(1000),
        fromDate: new Date('2025-03-10'),
        toDate: new Date('2025-07-10'),
        projectId: 'P-2007',
        projectName: 'Health Literacy Study',
        statusName: 'Finalized'
      },
      {
        id: 'A-1008',
        name: 'GIS Mapping Support',
        jobDescription: lorem(1000),
        fromDate: new Date('2025-02-05'),
        toDate: new Date('2025-04-05'),
        projectId: 'P-2008',
        projectName: 'Disease Surveillance Mapping',
        statusName: 'Signed'
      },
      {
        id: 'A-1009',
        name: 'Community Engagement',
        jobDescription: 'Plan and conduct community meetings.',
        fromDate: new Date('2025-01-25'),
        toDate: new Date('2025-03-25'),
        projectId: 'P-2009',
        projectName: 'Health Promotion Campaign',
        statusName: 'Draft'
      },
      {
        id: 'A-1010',
        name: 'Budget Tracking',
        jobDescription: 'Track project expenses and budget utilization.',
        fromDate: new Date('2025-02-18'),
        toDate: new Date('2025-06-18'),
        projectId: 'P-2010',
        projectName: 'Facility Upgrade Project',
        statusName: 'Finalized'
      }
    ];

    const sortKey = (sortColumn || 'ProjectName').toLowerCase();
    const itemsSorted = [...mockItems].sort((a, b) => {
      const getVal = (x: Assignment): any => {
        switch (sortKey) {
          case 'projectname': return x.projectName?.toString().toLowerCase();
          case 'name': return x.name?.toString().toLowerCase();
          case 'fromdate': return x.fromDate?.valueOf();
          case 'todate': return x.toDate?.valueOf();
          case 'statusname': return x.statusName?.toString().toLowerCase();
          default: return x.projectName?.toString().toLowerCase();
        }
      };
      const av = getVal(a);
      const bv = getVal(b);
      if (av < bv) return -1;
      if (av > bv) return 1;
      return 0;
    });

    const total = itemsSorted.length;
    const currentPage = Math.max(1, Math.floor(page || 1));
    const pageSize = Math.max(1, Math.floor(limit || 10));
    const start = (currentPage - 1) * pageSize;
    const end = start + pageSize;
    const paged = itemsSorted.slice(start, end);

    const response: BaseModel<BaseListResponse<Assignment>> = {
      result: true,
      item: {
        isClientSide: true,
        total: total,
        displayTotal: paged.length,
        items: paged,
        summary: null
      }
    };

    return of(response);
  }
}
